import CloseIcon from '@mui/icons-material/Close';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import { Theme } from '@mui/material/styles';
import { useModal } from 'mui-modal-provider';
import * as React from 'react';
import { makeStyles } from 'tss-react/mui';
import {
    useAssignEmailSettings,
    useDisconnectEmailSettings,
    useTestEmailSettings,
} from '../../../../sic-lib/api/hooks/useUserEmailSettings';
import { USER_EMAIL_SETTINGS_DEFAULT } from '../../../../sic-lib/defaults';
import { EmailSettingsStatus } from '../../../../sic-lib/enums/EmailSettingsStatus';
import { EmailSettings } from '../../../../sic-lib/types/EmailSettings';
import { useAppDispatch } from '../../../../store/hooks';
import { createToast } from '../../../../store/notify';
import { createErrorToast } from '../../../../utils/notifyHelpers';
import AxiosErrorAlert from '../../../Shared/Alerts/AxiosErrorAlert';
import ButtonLoading from '../../../Shared/ButtonLoading';
import DialogConfirmAction from '../../../Shared/DialogConfirmAction/DialogConfirmAction';

interface Props {
    isShowAdvanced: boolean;
    onShowAdvanced: React.Dispatch<boolean>;
    onSettingsChange: React.Dispatch<Partial<EmailSettings>>;
    userEmailSettings: EmailSettings;
}

export default function UserEmailButtonGroup({
    isShowAdvanced,
    onShowAdvanced,
    onSettingsChange,
    userEmailSettings,
}: Props) {
    const { classes } = useStyles();
    const { showModal } = useModal();
    const dispatch = useAppDispatch();

    const {
        mutate: AssignEmailSettingsMutation,
        isLoading: isAssignLoading,
        isSuccess: isAssignSuccess,
        isError: isAssignError,
        error: assignError,
    } = useAssignEmailSettings();

    const {
        mutate: testEmailSettingsMutation,
        isLoading: isTestingLoading,
        isSuccess: isTestingSuccess,
        isError: isTestingError,
        error: testError,
    } = useTestEmailSettings();

    const {
        mutate: disconnectEmailMutation,
        isLoading: isDisconnectLoading,
        isSuccess: isDisconnectSuccess,
        isError: isDisconnectError,
    } = useDisconnectEmailSettings();

    const handleDisconnectConfirm = () => {
        const modal = showModal(DialogConfirmAction, {
            title: 'Confirm Disconnect Account',
            description:
                'Once disconnected, you will not be able to manage your email conversation history with your leads.',
            onConfirm: async () => {
                // Added empty string so we can use the second argument of the mutation.
                // it is not doing anything on the mutation function.
                disconnectEmailMutation('', {
                    onSuccess: () => {
                        onSettingsChange(USER_EMAIL_SETTINGS_DEFAULT);
                        onShowAdvanced(false);
                    },
                });
            },
            onCancel: () => {
                modal.hide();
            },
        });
    };

    const handleTestAndSave = () => {
        const check = checkHandler();
        if (check) {
            testEmailSettingsMutation(userEmailSettings, {
                onSuccess: () => {
                    AssignEmailSettingsMutation(userEmailSettings);
                    onSettingsChange({ ...userEmailSettings, status: 1 });
                },
            });
        }
    };

    const checkHandler = () => {
        let check = true;

        if (
            !!userEmailSettings.emailUserPassword === false ||
            !!userEmailSettings.emailUserName === false
        ) {
            const toastData = createErrorToast('Email AND/OR password can not be empty');
            dispatch(createToast({ toastData }));
            check = false;
        }
        if (!!userEmailSettings.imapHost === false || !!userEmailSettings.imapPort === false) {
            const toastData = createErrorToast('Imap host AND/OR port can not be empty');
            dispatch(createToast({ toastData }));
            check = false;
            onShowAdvanced(true);
        }
        if (!!userEmailSettings.smtpHost === false || !!userEmailSettings.smtpPort === false) {
            const toastData = createErrorToast('Smtp host AND/OR port can not be empty');
            dispatch(createToast({ toastData }));
            check = false;
            onShowAdvanced(true);
        }

        return check;
    };

    return (
        <Grid container item direction="row" spacing={2} justifyContent="flex-end">
            {userEmailSettings.status === EmailSettingsStatus.Enabled && (
                <Grid item>
                    <ButtonLoading
                        isLoading={isDisconnectLoading}
                        isSuccess={isDisconnectSuccess}
                        successLabel="Disconnected"
                        isError={isDisconnectError}
                        variant="contained"
                        color="error"
                        onClick={handleDisconnectConfirm}
                        startIcon={<CloseIcon />}
                    >
                        Disconnect Account
                    </ButtonLoading>
                </Grid>
            )}
            {assignError && (
                <Grid item>
                    <AxiosErrorAlert error={assignError} />
                </Grid>
            )}
            {testError && (
                <Grid item>
                    <AxiosErrorAlert error={testError} />
                </Grid>
            )}
            <Grid item>
                <Button variant="text" onClick={() => onShowAdvanced(!isShowAdvanced)}>
                    {isShowAdvanced
                        ? 'Hide Advanced Settings'
                        : 'Show Advanced Email Provider Settings'}
                </Button>
            </Grid>
            <Grid item>
                <ButtonLoading
                    isLoading={isTestingLoading || isAssignLoading}
                    variant="contained"
                    isSuccess={isAssignSuccess || isTestingSuccess}
                    successLabel="Connected"
                    isError={isAssignError || isTestingError}
                    errorLabel="Error Saving"
                    color="success"
                    onClick={handleTestAndSave}
                    startIcon={<SaveOutlinedIcon />}
                >
                    Test & Save
                </ButtonLoading>
            </Grid>
        </Grid>
    );
}

const useStyles = makeStyles()((theme: Theme) => ({}));
